// Client-side coalesce window. Default 0 = immediate submit (industry standard,
// matches Vercel AI SDK + Intercom/Crisp/Drift). The 2500ms default from the
// original spec (section 3.6 + 7.2) was adding 2500ms latency to every single-
// message turn without meaningful benefit -- the InputBar's status-driven
// disable already prevents true rapid-fire (status flips to 'submitted'
// immediately on send, blocking the next Enter until streaming completes).
//
// Vendors that genuinely want batching can override per-vendor via the
// chat-session response's coalesce_window_ms field (already wired through
// routers/chat.py:387).
//
// The useCoalesce hook below still functions as a no-op pass-through when
// windowMs=0 (queue calls fire() synchronously), so removing the timer
// doesn't break the buffer/flush/cancel API surface used by App.tsx.

import { useCallback, useRef } from 'react'

export interface CoalesceAPI {
  queue: (text: string) => void
  flush: () => void
  cancel: () => void
}

export function resolveCoalesceWindow(serverValue: number | null | undefined): number {
  if (serverValue === 0) return 0  // explicit bypass
  if (typeof serverValue === 'number') return serverValue
  return 0  // was 2500 prior to 2026-05-13; see header comment
}

export function useCoalesce(windowMs: number, onFire: (text: string) => void): CoalesceAPI {
  const buffer = useRef<string[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fire = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    if (buffer.current.length === 0) return
    const text = buffer.current.join('\n')
    buffer.current = []
    onFire(text)
  }, [onFire])

  const queue = useCallback((text: string) => {
    buffer.current.push(text)
    if (windowMs === 0) {
      fire()
      return
    }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(fire, windowMs)
  }, [windowMs, fire])

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    buffer.current = []
  }, [])

  return { queue, flush: fire, cancel }
}
